import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import {
    defineMessages,
    useIntl,
    FormattedNumber,
    FormattedMessage,
} from 'react-intl';
import { withSize } from 'react-sizeme';
import { observer } from 'mobx-react-lite';

import {
    Sizes,
    Colours,
    MessagePropType,
    ProviderDetails,
    LegacyCityPropType,
    PageBehaviourButton,
    IconChooser,
} from '@ratehub/base-ui';
import {
    RATEHUB_BROKERAGE_IDS,
    useApplyForRateCallback,
} from '@ratehub/mtg-common';

import ProviderRatesPropType from '../definitions/ProviderRatesPropType';
import ProductTable from './ProductTable';


function ProductTableMortgageProvider({
    title,
    lastUpdatedDate,
    hideTimestamp,

    columnHeaders,
    bestRates,
    providerRates,

    provinceCode,
    city,
    locationSelectorLabel,

    onChangeLocation,
    isFetching,

    ctaLabel,
    ctaHREF,
    providerRateCTALabel,

    size,

    className,
}) {
    const intl = useIntl();
    const applyForRate = useApplyForRateCallback();

    function handleModalCallback(rate) {
        applyForRate({
            rate,
            rateFetchingOptions: {
                city,
            },
        });
    }

    const columns = [
        {
            label: intl.formatMessage(MESSAGES.PROVIDER_TABLE_HEADER),
            hideBelowWidth: undefined,
            fullRowBelowWidth: undefined,
            align: 'left',
        },
        ...columnHeaders.map(header => ({
            label: intl.formatMessage(MESSAGES.TERM_MESSAGES, {
                term: header.term,
                type: header.type,
            }),
            hideBelowWidth: header.hiddenBelowWidth ? 540 : undefined,
            fullRowBelowWidth: undefined,
            align: 'center',
        })),
    ];

    const rows = providerRates.map(provider => ({
        // Don't have a rate object but this is a good start. Can update for Naman if he needs.
        analyticsObject: provider,
        htmlCellContents: buildProviderRow(
            provider,
            providerRateCTALabel,
            handleModalCallback,
        ),
    }));

    // Put the best rates as the first row on the table if it's provided
    if (bestRates) {
        const bestRateRow = {
            // Don't have a rate object but this is a good start. Can update for Naman if he needs.
            analyticsObject: bestRates,
            backgroundColour: Colours.MINT_LIGHT,
            htmlCellContents: buildBestRateRow(
                bestRates,
                providerRateCTALabel,
                handleModalCallback,
            ),
        };

        rows.unshift(bestRateRow);
    }

    return (
        <Container
            size={size}
            className={className}
        >
            <ProductTable
                title={title}
                lastUpdatedDate={lastUpdatedDate}
                hideTimestamp={hideTimestamp}
                showAdDisclosure
                locationSelectorLabel={locationSelectorLabel}
                initialProvinceCode={provinceCode}
                initialCity={city}
                onChangeLocation={onChangeLocation}
                columns={columns}
                rows={rows}
                tableCtaMessage={ctaLabel}
                tableCtaHref={ctaHREF}
                showSpinner={isFetching}
                isAlwaysExpanded
            />
        </Container>
    );
}

ProductTableMortgageProvider.propTypes = {
    title: PropTypes.string.isRequired,
    lastUpdatedDate: PropTypes.string.isRequired,
    hideTimestamp: PropTypes.bool,
    locationSelectorLabel: MessagePropType.isRequired,

    columnHeaders: PropTypes.arrayOf(
        PropTypes.shape({
            term: PropTypes.number.isRequired,
            type: PropTypes.string.isRequired,
            hiddenBelowWidth: PropTypes.bool,
        }),
    ).isRequired,
    bestRates: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.number.isRequired,
            providerSlug: PropTypes.string.isRequired,
            deltaPrime: PropTypes.number,
        }),
    ),
    providerRates: ProviderRatesPropType.isRequired,

    provinceCode: PropTypes.string.isRequired,
    city: LegacyCityPropType.isRequired,

    onChangeLocation: PropTypes.func.isRequired,

    ctaLabel: MessagePropType.isRequired,
    ctaHREF: PropTypes.string.isRequired,
    providerRateCTALabel: MessagePropType.isRequired,

    isFetching: PropTypes.bool,

    size: PropTypes.object.isRequired,

    className: PropTypes.string,
};

ProductTableMortgageProvider.defaultProps = {
    hideTimestamp: false,
    bestRates: undefined,
    isFetching: false,
    className: undefined,
};

const ResponsiveCell = css`
    > .providerName {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        font-size: ${Sizes.FONTS['2XS']};
        max-width: 6rem;

        > .provider-details {
            flex: 1;
        }
    }
`;

const Container = styled.div`
    position: relative;

    .rateCell {
        min-height: 120px;
        display: flex;
        flex-direction: column;
        justify-content: center;

        > .rate {
            display: flex;
            flex-direction: column;
        }
    }

    .providerCell {
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        > .providerName {
            text-decoration: none;
            color: ${Colours.BLACKBERRY};
        }

        ${props => props.size?.width <= 700 && ResponsiveCell}
    }
`;

function buildBestRateRow(data, ctaLabel, leadFormCallback) {
    const bestRates = data.map(rate => {
        return rate
            ? {
                content: (
                    <div className="rateCell">
                        <div className="rate">
                            <p className="rh-title-2xl rh-m-0">
                                <FormattedNumber
                                    value={rate.value / 100}
                                    format="percent2"
                                />
                            </p>
                            <If condition={rate.deltaPrime}>
                                <p className="rh-text-s rh-m-0">
                                    <FormattedMessage
                                        {...MESSAGES.PRIME_DIFFERENCE_LABEL}
                                        values={{
                                            delta: formatDifferenceFromPrime(rate.deltaPrime),
                                        }}
                                    />
                                </p>
                            </If>
                        </div>

                        <PageBehaviourButton
                            className="rh-mx-auto weight-medium rh-mb-1 rh-mt-auto rh-text-s"
                            data-name="mtg-providerTable-inquire-cta"
                            message={ctaLabel}
                            size="small"
                            onClick={() => leadFormCallback(rate)}
                        />
                    </div>
                ),
            }
            : {
                content: <div />,
            };
    });
    return [
        {
            content: (
                <div className="providerCell trending">
                    <IconChooser
                        iconKey="trending"
                        size="M"
                    />

                    <p className="rh-ml-1_5 weight-bold">
                        <FormattedMessage
                            id="web-components.MortgageProviderRatesTable.bestMarketRateLabel"
                            defaultMessage="Best market rate"
                        />
                    </p>
                </div>
            ),
        },
        ...bestRates,
    ];
}

function buildProviderRow(data, ctaLabel, leadFormCallback) {
    const rateCells = data.rates.map(rate => {
        return rate
            ? {
                content: (
                    <div className="rateCell">
                        <div className="rate">
                            <p className="rh-title-2xl rh-m-0 weight-bold">
                                <FormattedNumber
                                    value={rate.value / 100}
                                    format="percent2"
                                />
                            </p>

                            <If condition={rate.deltaPrime}>
                                <p className="rh-text-s rh-m-0">
                                    <FormattedMessage
                                        {...MESSAGES.PRIME_DIFFERENCE_LABEL}
                                        values={{
                                            delta: formatDifferenceFromPrime(rate.deltaPrime),
                                        }}
                                    />
                                </p>
                            </If>
                        </div>

                        <PageBehaviourButton
                            className="rh-mx-auto weight-medium rh-mb-1 rh-mt-auto rh-text-s"
                            data-name="mtg-providerTable-inquire-cta"
                            message={ctaLabel}
                            size="small"
                            onClick={() => leadFormCallback(rate.rate)}
                        />
                    </div>
                ),
            }
            : {
                content: <div />,
            };
    });

    return [
        {
            content: (
                <div className="providerCell">
                    <a
                        className="providerName"
                        data-name="mtg-productTableMortgageProvider-seeProviderDetails"
                        href={data.provider.url}
                    >
                        <ProviderDetails
                            providerName={data?.provider?.name}
                            providerIcon={data.provider.logo}
                            ratingPercent={data?.provider?.rating * 20}
                            reviewCount={data?.provider?.reviews}
                            isRatehubCompany={Object.values(
                                RATEHUB_BROKERAGE_IDS,
                            ).includes(data?.provider?.id)}
                            className="provider-details"
                        />
                    </a>
                </div>
            ),
        },
        ...rateCells,
    ];
}

function formatDifferenceFromPrime(value) {
    const percentage = value / 100;
    return value < 0 ? percentage : `+${percentage}`;
}

const MESSAGES = defineMessages({
    PROVIDER_TABLE_HEADER: {
        id: 'web-components.MortgageProviderRatesTable.providerTableHeader',
        defaultMessage: 'Provider',
    },
    PRIME_DIFFERENCE_LABEL: {
        id: 'web-components.MortgageProviderRatesTable.differenceFromPrime',
        defaultMessage: 'Prime {delta, number, percent2}',
    },
    TERM_MESSAGES: {
        id: 'web-components.MortgageProviderRatesTable.termMessages',
        defaultMessage: '{term} Year {type}',
    },
});

export default withSize({ noPlaceholder: true })(
    observer(ProductTableMortgageProvider),
);
